import { useSuspenseQuery } from "@tanstack/react-query";
import { createFileRoute, redirect, useNavigate } from "@tanstack/react-router";
import SettingsView from "@/components/settings/SettingsView";
import {
  SETTINGS_SECTIONS,
  canOpenSettingsSection,
  firstSettingsTab,
  settingsTabFromAdminTab,
} from "@/components/settings/settings-nav";
import { readLastPlace } from "@/lib/last-place";
import type { SettingsTab } from "@/lib/search-params";
import { trpc } from "@/lib/trpc";
import { useDocumentTitle } from "@/lib/window-title";

// Global admin now lives in Settings, as the "All Companies" sections. `/admin`
// (and its old `?tab=brands|companies|connections|people|launch` links) opens
// the matching section under the person's Last place Company, else their first
// Company. Someone with no Company at all gets Settings here, in a page of its
// own: the All Companies sections they may open and their own (Connected apps),
// which every signed-in person has; the server still decides what each section
// may return.
export const Route = createFileRoute("/_authenticated/admin")({
  validateSearch: (search: Record<string, unknown>): { tab: SettingsTab } => ({
    tab: settingsTabFromAdminTab(search.tab),
  }),
  beforeLoad: async ({ context, search }) => {
    const viewer = await context.queryClient.ensureQueryData(
      trpc.organization.viewer.queryOptions(),
    );
    const remembered = readLastPlace()?.orgSlug;
    const org =
      viewer.organizations.find((item) => item.slug === remembered) ?? viewer.organizations[0];
    if (org) {
      throw redirect({
        to: "/$orgSlug/settings",
        params: { orgSlug: org.slug },
        search: { tab: search.tab },
        replace: true,
      });
    }
  },
  component: AllCompaniesSettingsPage,
});

function AllCompaniesSettingsPage() {
  const { data: viewer } = useSuspenseQuery(trpc.organization.viewer.queryOptions());
  const { tab: requested } = Route.useSearch();
  const navigate = useNavigate();
  const tab = canOpenSettingsSection(requested, null, viewer)
    ? requested
    : firstSettingsTab(null, viewer);
  useDocumentTitle({
    section: "Settings",
    detail: SETTINGS_SECTIONS.find((section) => section.key === tab)?.label,
  });
  return (
    <SettingsView
      org={null}
      viewer={viewer}
      tab={tab}
      onTabChange={(next) => void navigate({ to: "/admin", search: { tab: next }, replace: true })}
    />
  );
}
